import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { 
  SiEthereum, 
  SiSolidity,
  SiIpfs,
  SiPolkadot,
  SiHiveBlockchain,
  SiBlockchaindotcom
} from 'react-icons/si';
import { 
  GiToken,
  GiTrade,
  GiCubes
} from 'react-icons/gi';
import {
  BsShieldCheck,
  BsCurrencyBitcoin,
  BsGear
} from 'react-icons/bs';
import ServiceModal from './ServiceModal';

// Add the animations
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

// Add the styled components
const ServicesContainer = styled.div`
  padding: 4rem 1rem;
  background: #0f172a;
  overflow-x: hidden;
  
  @media (min-width: 768px) {
    padding: 4rem 2rem;
  }
`;

const SectionTitle = styled.h2`
  text-align: center;
  font-size: 2rem;
  color: #e2e8f0;
  margin-bottom: 2rem;
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  
  @media (min-width: 768px) {
    font-size: 2.5rem;
    margin-bottom: 3rem;
  }
`;

const ServiceCategory = styled.div`
  margin-bottom: 3rem;
  padding: 0 0.5rem;
  
  @media (min-width: 768px) {
    margin-bottom: 4rem;
    padding: 0;
  }
`;

const CategoryTitle = styled.h3`
  font-size: 1.5rem;
  color: #e2e8f0;
  margin-bottom: 1.5rem;
  padding: 0 0.5rem;
  
  @media (min-width: 768px) {
    font-size: 1.8rem;
    margin-bottom: 2rem;
    padding: 0;
  }
`;

const ServiceGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
  justify-content: center;
  
  @media (min-width: 768px) {
    grid-template-columns: repeat(auto-fit, minmax(300px, 400px));
    gap: 2rem;
    justify-content: flex-start;
  }
`;

const ServiceCard = styled.div`
  max-width: 100%;
  width: auto;
  background: rgba(30, 41, 59, 0.5);
  border: 1px solid rgba(99, 102, 241, 0.2);
  border-radius: 12px;
  padding: 1.5rem;
  cursor: pointer;
  transition: all 0.3s ease;
  animation: ${fadeIn} 0.6s ease-out forwards;
  animation-delay: ${props => props.delay}s;
  opacity: 0;
  margin: 0 auto;
  
  @media (min-width: 768px) {
    max-width: 400px;
    width: 100%;
    padding: 2rem;
  }

  &:hover {
    transform: translateY(-5px);
    background: rgba(30, 41, 59, 0.8);
    border-color: ${props => props.accentColor || '#6366f1'};
  }
`;

const ServiceIcon = styled.div`
  font-size: 2rem;
  color: ${props => props.accentColor || '#6366f1'};
  margin-bottom: 1rem;
  
  @media (min-width: 768px) {
    font-size: 2.5rem;
    margin-bottom: 1.5rem;
  }
`;

const ServiceTitle = styled.h4`
  font-size: 1.25rem;
  color: #e2e8f0;
  margin-bottom: 0.75rem;
  color: ${props => props.accentColor || '#6366f1'};
  
  @media (min-width: 768px) {
    font-size: 1.5rem;
    margin-bottom: 1rem;
  }
`;

const ServiceDescription = styled.p`
  color: #94a3b8;
  margin-bottom: 1rem;
  line-height: 1.6;
  
  @media (min-width: 768px) {
    font-size: 1rem;
    margin-bottom: 1.5rem;
  }
`;

const ServiceFeatures = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  color: #94a3b8;

  li {
    margin-bottom: 0.4rem;
    display: flex;
    align-items: center;

    &:before {
      content: "•";
      color: ${props => props.accentColor || '#6366f1'};
      margin-right: 0.5rem;
    }
    
    @media (min-width: 768px) {
      font-size: 1rem;
      margin-bottom: 0.5rem;
    }
  }
`;

const Web3Services = () => {
  const [selectedService, setSelectedService] = useState(null);

  const categories = [
    {
      name: "AI Agent Framework Integration",
      services: [
        {
          title: 'AI-Powered Web3 Solutions',
          description: 'Integrate AI agents with blockchain technology for intelligent decentralized systems',
          features: [
            'Framework development',
            'AI-driven quantitative trading strategies development',
     
          ],
          modalContent: {
            detailedFeatures: [
              {
                category: "AI Integration",
                capabilities: [
                  "Autonomous smart contract execution",
                  "Machine learning for market analysis",
                  "Natural language processing for Web3",
                  "AI-powered risk management",
                  "Automated portfolio optimization"
                ]
              },
              {
                category: "Framework Development",
                capabilities: [
                  "Custom AI agent development",
                  "Multi-agent system architecture",
                  "Blockchain-AI bridge implementation",
                  "Scalable agent networks",
                  "Real-time decision systems"
                ]
              }
            ]
          },
          accentColor: '#6366f1',
          icon: <SiSolidity />
        }
      ]
    },
    {
      name: "DeFi Protocols",
      services: [
        {
          title: 'DeFi Protocol Development',
          description: 'Build secure and efficient decentralized financial protocols',
          features: [
            'Lending & borrowing platforms',
            'Automated market makers',
            'Yield optimization protocols',
            'Cross-chain DeFi solutions'
          ],
          modalContent: {
            detailedFeatures: [
              {
                category: "Protocol Types",
                capabilities: [
                  "Decentralized exchanges (DEX)",
                  "Yield farming protocols",
                  "Stablecoin mechanisms",
                  "Liquidity pools",
                  "Flash loan protocols"
                ]
              }
            ]
          },
          accentColor: '#8b5cf6',
          icon: <SiBlockchaindotcom />
        }
      ]
    },
    {
      name: "GameFi Development",
      services: [
        {
          title: 'Blockchain Gaming Solutions',
          description: 'Create engaging Play-to-Earn and GameFi experiences',
          features: [
            'P2E game development',
            'In-game token economics',
            'NFT integration in games',
            'Cross-game asset systems'
          ],
          modalContent: {
            detailedFeatures: [
              {
                category: "Game Development",
                capabilities: [
                  "Unity/Unreal Engine integration",
                  "On-chain game development",
                  "Blockchain game mechanics",
                  "Token reward systems",
                  "Game asset tokenization",
                  "Multiplayer infrastructure"
                ]
              }
            ]
          },
          accentColor: '#14b8a6',
          icon: <SiIpfs />
        }
      ]
    },
    {
      name: "NFT",
      services: [
        {
          title: 'NFT Development & Integration',
          description: 'Comprehensive NFT solutions for digital assets and marketplaces',
          features: [
            'AI Agent Framework for automated NFT creation, mint, pricing, and marketplace',
            'Multi-chain NFT bridges',
          ],
          modalContent: {
            detailedFeatures: [
              {
                category: "NFT Infrastructure",
                capabilities: [
                  "ERC-721/ERC-1155 development",
                  "Marketplace creation",
                  "Auction mechanisms",
                  "Royalty systems",
                  "IPFS integration"
                ]
              }
            ]
          },
          accentColor: '#ec4899',
          icon: <GiCubes />
        }
      ]
    }
  ];

  return (
    <ServicesContainer>
      <SectionTitle>Our Web3 Development Services</SectionTitle>
      {categories.map((category, categoryIndex) => (
        <ServiceCategory key={categoryIndex}>
          <CategoryTitle>{category.name}</CategoryTitle>
          <ServiceGrid>
            {category.services.map((service, serviceIndex) => (
              <ServiceCard 
                key={serviceIndex}
                delay={serviceIndex * 0.1}
                accentColor={service.accentColor}
                onClick={() => setSelectedService(service)}
              >
                <ServiceIcon accentColor={service.accentColor}>
                  {service.icon}
                </ServiceIcon>
                <ServiceTitle accentColor={service.accentColor}>
                  {service.title}
                </ServiceTitle>
                <ServiceDescription>{service.description}</ServiceDescription>
                <ServiceFeatures accentColor={service.accentColor}>
                  {service.features.map((feature, idx) => (
                    <li key={idx}>{feature}</li>
                  ))}
                </ServiceFeatures>
              </ServiceCard>
            ))}
          </ServiceGrid>
        </ServiceCategory>
      ))}
      
      {selectedService && (
        <ServiceModal 
          service={selectedService} 
          onClose={() => setSelectedService(null)}
        />
      )}
    </ServicesContainer>
  );
};

export default Web3Services; 